import { useState } from 'react'
import { api, ErroreApi, type StatoSecondoFattore } from '../servizi/api.ts'
import { useSessione } from '../servizi/sessione.tsx'
import { Avviso, Intestazione, Pie } from '../componenti/Pezzi.tsx'
import { Bottone, Campo, Modale, Riquadro, Spunta } from '../componenti/base.tsx'
import { DisegnoQr } from '../componenti/DisegnoQr.tsx'

/**
 * Pagina «Secondo fattore» (decisioni di Davide del 31/10/2026, 493).
 *
 * Senza secondo fattore: si attiva con il codice QR (o la chiave scritta a mano) e il primo codice
 * dell'app; poi compaiono dieci codici di recupero, una volta sola, da stampare o copiare. Con il
 * secondo fattore attivo: «Genera nuovi codici di recupero» e «Sostituisci l'app», che l'API concede
 * solo a una sessione che l'ha gia' superato.
 *
 * Due forme: la pagina dentro il gestionale, dal menu «Il tuo accesso», e la pagina a schermo intero
 * che main.tsx mostra quando l'obbligo e' in vigore e il fattore non e' attivo, come per la password
 * provvisoria (CambioPassword.tsx).
 */

const messaggio = (e: unknown) =>
  e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.'

/** AAAA-MM-GG → gg/mm/aaaa. */
const inItaliano = (giorno: string) => giorno.split('-').reverse().join('/')

/** Porta il foco sul campo del codice appena compare (come in Accesso.tsx). */
function focoIniziale(elemento: HTMLInputElement | null) {
  elemento?.focus()
}

/** Che cosa dice l'obbligo per gli incarichi dell'utente, in una frase. */
function Obbligo({ stato }: { stato: StatoSecondoFattore }) {
  if (stato.daAttivareOra) {
    return (
      <Avviso tipo="attenzione">
        Per i tuoi incarichi il secondo fattore è obbligatorio
        {stato.obbligatorioDal ? ` dal ${inItaliano(stato.obbligatorioDal)}` : ''}: senza, il gestionale non si
        apre. Bastano due minuti.
      </Avviso>
    )
  }
  if (stato.daAttivareEntro && stato.obbligatorioDal) {
    return (
      <Avviso tipo="attenzione">
        Per i tuoi incarichi il secondo fattore è obbligatorio dal {inItaliano(stato.obbligatorioDal)}.
        Attivalo entro il {inItaliano(stato.daAttivareEntro)}: dopo, senza, il gestionale non si apre.
      </Avviso>
    )
  }
  if (stato.obbligatorioDal) return <p>Per i tuoi incarichi il secondo fattore è obbligatorio.</p>
  return (
    <p>
      Per i tuoi incarichi non è obbligatorio. Ti consigliamo di attivarlo lo stesso: protegge la tua utenza
      anche se qualcuno scopre la password.
    </p>
  )
}

/**
 * Collegare l'app: preparazione, codice QR e chiave, primo codice. La preparazione parte con un
 * comando e non all'apertura: chiederla due volte darebbe due segreti, e l'app inquadrerebbe il
 * primo mentre il gestionale aspetta il secondo.
 *
 * `fatto` riceve i codici di recupero alla prima attivazione, null quando si e' sostituita l'app.
 */
function Configurazione({ sostituzione, fatto, annulla }: {
  sostituzione: boolean
  fatto: (codici: string[] | null) => void
  annulla?: () => void
}) {
  const { io } = useSessione()
  const [preparato, setPreparato] = useState<{ segreto: string; uri: string } | null>(null)
  const [codice, setCodice] = useState('')
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  async function esegui(azione: () => Promise<void>) {
    setErrore(null); setInCorso(true)
    try { await azione() } catch (e) { setErrore(messaggio(e)) } finally { setInCorso(false) }
  }
  const prepara = () => esegui(async () => {
    setPreparato(await api.post<{ segreto: string; uri: string }>('/auth/mfa/prepara'))
    setCodice('')
  })
  const conferma = () => esegui(async () => {
    const r = await api.post<{ codici: string[] | null }>('/auth/mfa/conferma', { codice })
    fatto(r.codici)
  })

  if (!preparato) {
    return (
      <div className="fattore-passo">
        {errore && <Avviso tipo="errore">{errore}</Avviso>}
        <p>
          Ti serve {sostituzione ? 'il telefono nuovo' : 'il telefono'} con un’app di autenticazione, per esempio
          Google Authenticator, Microsoft Authenticator o FreeOTP. Se non ce l’hai, scaricala dallo store.
        </p>
        <div className="fattore-azioni">
          <Bottone variante="primario" inCorso={inCorso} onClick={() => void prepara()}>
            {sostituzione ? 'Collega l’app nuova' : 'Comincia'}
          </Bottone>
          {annulla && <Bottone variante="discreto" onClick={annulla}>Annulla</Bottone>}
        </div>
      </div>
    )
  }

  return (
    <div className="fattore-passo">
      <ol className="fattore-istruzioni">
        <li>Nell’app premi «Aggiungi» (o «+») e inquadra il codice.</li>
        <li>L’app mostra «CIDIS ERP» e un codice di sei cifre che cambia ogni trenta secondi.</li>
        <li>Scrivi qui sotto il codice che vedi adesso e premi «Conferma».</li>
      </ol>
      <DisegnoQr testo={preparato.uri} etichetta="Codice QR da inquadrare con l’app di autenticazione" />
      <p className="fattore-nota">
        Non riesci a inquadrarlo, o usi il gestionale dal telefono? Nell’app scegli di inserire la chiave a mano
        e scrivi:
      </p>
      <p className="fattore-segreto">{preparato.segreto.match(/.{1,4}/g)?.join(' ')}</p>
      <p className="fattore-nota">Nome dell’account: {io?.email}. Tipo: basato sul tempo.</p>

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <form onSubmit={(e) => { e.preventDefault(); void conferma() }}>
        <div className="campo-codice">
          <Campo etichetta="Codice dell’app" valore={codice}
                 cambia={(v) => setCodice(v.replace(/\D/g, ''))}
                 autoComplete="one-time-code" modalitaTastiera="numeric" maxLength={6}
                 obbligatorio riferimento={focoIniziale} />
        </div>
        <div className="fattore-azioni">
          <Bottone tipo="submit" variante="primario" inCorso={inCorso} disabled={codice.length !== 6}>
            Conferma
          </Bottone>
          {annulla && <Bottone variante="discreto" onClick={annulla}>Annulla</Bottone>}
        </div>
      </form>
      {sostituzione && (
        <p className="fattore-nota">Finché non confermi, per entrare vale l’app di prima.</p>
      )}
    </div>
  )
}

/**
 * I codici di recupero appena generati: si vedono una volta sola. Si stampano (solo questo riquadro,
 * stile-secondo-fattore.css) o si copiano; per andare avanti si dichiara di averli conservati.
 */
function CodiciDiRecupero({ codici, fatto, testoFatto }: {
  codici: string[]
  fatto: () => void
  testoFatto: string
}) {
  const { io } = useSessione()
  const [conservati, setConservati] = useState(false)
  const [copiati, setCopiati] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const oggi = new Date().toLocaleDateString('it-IT', { timeZone: 'Europe/Rome' })

  async function copia() {
    try {
      await navigator.clipboard.writeText(
        `Codici di recupero del gestionale CIDIS (${io?.email ?? ''}, ${oggi})\n${codici.join('\n')}`)
      setCopiati(true)
    } catch {
      setErrore('Il browser non ha permesso di copiare: stampali, oppure scrivili a mano.')
    }
  }

  function stampa() {
    document.documentElement.classList.add('fattore-stampa')
    const togli = () => {
      document.documentElement.classList.remove('fattore-stampa')
      window.removeEventListener('afterprint', togli)
    }
    window.addEventListener('afterprint', togli)
    window.print()
  }

  return (
    <div className="fattore-passo">
      <Avviso tipo="attenzione">
        Questi codici si vedono adesso e mai più. Ognuno vale una volta sola, al posto del codice dell’app,
        se non hai il telefono con te. Stampali o copiali e tienili in un posto sicuro, lontano dal telefono.
      </Avviso>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <div className="fattore-da-stampare">
        <p className="fattore-solo-stampa">
          Codici di recupero del gestionale CIDIS · {io?.email} · {oggi}
        </p>
        <ol className="fattore-codici">
          {codici.map((c) => <li key={c}><code>{c}</code></li>)}
        </ol>
        <p className="fattore-solo-stampa">
          Ogni codice vale una volta sola. Quando ne restano pochi, generane di nuovi dalla pagina «Secondo
          fattore».
        </p>
      </div>
      <div className="fattore-azioni">
        <Bottone onClick={stampa}>Stampa</Bottone>
        <Bottone onClick={() => void copia()}>{copiati ? 'Copiati' : 'Copia'}</Bottone>
      </div>
      <Spunta etichetta="Ho stampato o copiato i codici e li tengo in un posto sicuro"
              valore={conservati} cambia={setConservati} />
      <div className="fattore-azioni">
        <Bottone variante="primario" disabled={!conservati} onClick={fatto}>{testoFatto}</Bottone>
      </div>
    </div>
  )
}

/** La pagina dentro il gestionale. */
export function SecondoFattore() {
  const { io, aggiorna } = useSessione()
  const [fase, setFase] = useState<'stato' | 'attiva' | 'sostituisci' | 'codici'>('stato')
  const [codici, setCodici] = useState<string[] | null>(null)
  const [chiediNuovi, setChiediNuovi] = useState(false)
  const [esito, setEsito] = useState<string | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const stato = io?.secondoFattore
  if (!stato) return null

  function configurato(nuovi: string[] | null) {
    if (nuovi) {
      setCodici(nuovi); setFase('codici')
    } else {
      setFase('stato')
      setEsito('Fatto: d’ora in poi vale il codice dell’app nuova. I codici di recupero restano quelli di prima.')
      void aggiorna()
    }
  }

  async function generaNuovi() {
    setErrore(null); setInCorso(true)
    try {
      const r = await api.post<{ codici: string[] }>('/auth/mfa/codici-recupero')
      setChiediNuovi(false); setEsito(null); setCodici(r.codici); setFase('codici')
    } catch (e) {
      setErrore(messaggio(e))
    } finally {
      setInCorso(false)
    }
  }

  const finito = () => { setCodici(null); setFase('stato'); void aggiorna() }
  const rimasti = stato.codiciRimasti

  return (
    <>
      <Intestazione titolo="Secondo fattore"
                    sottotitolo="Dopo la password, il codice dell’app di autenticazione conferma che sei proprio tu." />
      {esito && <Avviso tipo="successo">{esito}</Avviso>}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      {fase === 'codici' && codici ? (
        <Riquadro titolo="I tuoi codici di recupero">
          <CodiciDiRecupero codici={codici} testoFatto="Fatto" fatto={finito} />
        </Riquadro>
      ) : !stato.attivo ? (
        <Riquadro titolo="Attiva il secondo fattore">
          <Obbligo stato={stato} />
          {fase === 'attiva'
            ? <Configurazione sostituzione={false} fatto={configurato} annulla={() => setFase('stato')} />
            : (
              <div className="fattore-azioni">
                <Bottone variante="primario" onClick={() => { setEsito(null); setFase('attiva') }}>
                  Attiva il secondo fattore
                </Bottone>
              </div>
            )}
        </Riquadro>
      ) : (
        <>
          <Riquadro titolo="Stato">
            <p>Il secondo fattore è attivo: dopo la password il gestionale chiede il codice dell’app.</p>
            <Obbligo stato={stato} />
          </Riquadro>

          <Riquadro titolo="Codici di recupero">
            <p>
              Servono per entrare se non hai il telefono: nella pagina di accesso scegli «Usa un codice di
              recupero». {rimasti !== null && <>Te ne restano <strong>{rimasti}</strong> su 10.</>}
            </p>
            {rimasti !== null && rimasti <= 3 && (
              <Avviso tipo="attenzione">Ne restano pochi: genera i codici nuovi e conservali.</Avviso>
            )}
            <div className="fattore-azioni">
              <Bottone onClick={() => { setErrore(null); setChiediNuovi(true) }}>Genera nuovi codici di recupero</Bottone>
            </div>
          </Riquadro>

          <Riquadro titolo="Sostituisci l’app">
            {fase === 'sostituisci'
              ? <Configurazione sostituzione fatto={configurato} annulla={() => setFase('stato')} />
              : (
                <>
                  <p>
                    Per un telefono nuovo o un’altra app. Fino alla conferma vale l’app di prima. Se hai perso il
                    telefono e non hai più codici di recupero, chiedi a chi gestisce gli utenti di azzerare il
                    secondo fattore.
                  </p>
                  <div className="fattore-azioni">
                    <Bottone onClick={() => { setEsito(null); setFase('sostituisci') }}>Sostituisci l’app</Bottone>
                  </div>
                </>
              )}
          </Riquadro>
        </>
      )}

      {chiediNuovi && (
        <Modale titolo="Genera nuovi codici di recupero" chiudi={() => setChiediNuovi(false)}
                azioni={<>
                  <Bottone variante="discreto" onClick={() => setChiediNuovi(false)}>Annulla</Bottone>
                  <Bottone variante="primario" inCorso={inCorso} onClick={() => void generaNuovi()}>Genera i codici</Bottone>
                </>}>
          <p>
            Ricevi dieci codici nuovi, da stampare o copiare subito. Quelli di prima, anche se li hai stampati,
            smettono di valere.
          </p>
        </Modale>
      )}
    </>
  )
}

/**
 * Obbligo in vigore e secondo fattore non attivo: la sola pagina che il gestionale apre, finche' non
 * lo si attiva (main.tsx). Dopo i codici di recupero si entra.
 */
export function SecondoFattoreObbligatorio() {
  const { io, aggiorna, esci } = useSessione()
  const [codici, setCodici] = useState<string[] | null>(null)
  const stato = io?.secondoFattore

  return (
    <div className="accesso">
      <div className="riquadro">
        <div className="scheda">
          <div className="stemma">
            <img src="/immagini/cidis-logo.png" alt="C.I.D.I.S. Impresa Sociale ETS" />
          </div>
          <h1>Secondo fattore</h1>
          <p className="occhiello">{codici ? 'I tuoi codici di recupero' : 'Da attivare prima di entrare'}</p>
          {codici
            ? <CodiciDiRecupero codici={codici} testoFatto="Entra nel gestionale" fatto={() => void aggiorna()} />
            : (
              <>
                {stato && <Obbligo stato={stato} />}
                <Configurazione sostituzione={false}
                                fatto={(nuovi) => { if (nuovi) setCodici(nuovi); else void aggiorna() }} />
              </>
            )}
          <div className="accesso-uscita">
            <Bottone variante="discreto" larghezzaPiena onClick={() => { void esci() }}>
              Esci
            </Bottone>
          </div>
        </div>
        <Pie />
      </div>
    </div>
  )
}
